import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Calculator, FileText, Truck, Receipt, 
  CreditCard, AlertTriangle, ClipboardCheck, Package,
  Trophy, Banknote, Shield, Share2
} from 'lucide-react';
import RequestSetupModal from '../components/ui/request-setup-modal';
import ShareModal from '../components/ui/share-modal';

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, status, href, color, category, onRequestClick }) => {
  const navigate = useNavigate();
  const isAvailable = status === 'available';
  const isRequest = status === 'request';
  
  const colorClasses = {
    emerald: { bg: 'bg-emerald-500', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600' },
    blue: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600' },
    orange: { bg: 'bg-orange-500', light: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600' }
  };
  
  const colors = colorClasses[color] || colorClasses.emerald;

  const handleClick = () => {
    if (isAvailable) {
      navigate(href);
    } else if (isRequest && onRequestClick) {
      onRequestClick();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={(isAvailable || isRequest) ? { y: -4 } : {}}
      className={`relative bg-white rounded-2xl p-6 border ${isAvailable ? colors.border : isRequest ? 'border-orange-200' : 'border-slate-200'} shadow-sm transition-all ${(isAvailable || isRequest) ? 'cursor-pointer hover:shadow-lg' : 'opacity-70'}`}
      onClick={handleClick}
    >
      {/* Category Badge */}
      <div className={`inline-flex items-center gap-1.5 ${colors.light} ${colors.text} px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4`}>
        {category === 'win' && <Trophy className="w-3 h-3" />}
        {category === 'deliver' && <Truck className="w-3 h-3" />}
        {category === 'paid' && <Banknote className="w-3 h-3" />}
        {category === 'win' ? 'Win Work' : category === 'deliver' ? 'Deliver Work' : 'Get Paid'}
      </div>

      {/* Icon */}
      <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
        <Icon className="w-7 h-7 text-white" />
      </div>

      {/* Content */}
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 mb-4 leading-relaxed">{description}</p>

      {/* Status */}
      {isAvailable ? (
        <div className="flex items-center gap-2 text-sm font-semibold text-orange-500">
          Learn more <ArrowRight className="w-4 h-4" />
        </div>
      ) : isRequest ? (
        <div className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-600 px-3 py-1.5 rounded-full text-xs font-semibold">
          Request <ArrowRight className="w-3 h-3" />
        </div>
      ) : (
        <div className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 px-3 py-1.5 rounded-full text-xs font-medium">
          Coming Soon
        </div>
      )}
    </motion.div>
  );
};

// Main Features Page
const Features = () => {
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const features = [
    // Win Work
    {
      icon: Calculator,
      title: 'Scope, Costing & Quote Assistant',
      description: 'Turn builder documents into a scope draft, costing checklist, and quote-ready output.',
      status: 'available',
      href: '/features/scope-costing-quote',
      color: 'emerald',
      category: 'win'
    },
    {
      icon: FileText,
      title: 'Leads & Referrals Assistant',
      description: 'Capture every lead and referral with voice notes, photos, and contact details.',
      status: 'available',
      href: '/features/leads-referrals',
      color: 'emerald',
      category: 'win'
    },
    // Deliver Work
    {
      icon: ClipboardCheck,
      title: 'SWMS & Safety Document Assistant',
      description: 'Generate compliant SWMS documents from job details and site conditions.',
      status: 'available',
      href: '/features/swms-safety',
      color: 'blue',
      category: 'deliver'
    },
    {
      icon: Truck,
      title: 'Site Update & Site Diary Assistant',
      description: 'Log site updates, progress notes, and variations with voice-first capture.',
      status: 'available',
      href: '/features/site-updates',
      color: 'blue',
      category: 'deliver'
    },
    {
      icon: AlertTriangle,
      title: 'Variation Capture Assistant',
      description: 'Document variations as they happen with photos, voice notes, and instant records.',
      status: 'available',
      href: '/features/variations',
      color: 'blue',
      category: 'deliver'
    },
    // Get Paid
    {
      icon: Receipt,
      title: 'Invoice & Progress Claim Assistant',
      description: 'Create invoices and progress claims from completed work records.',
      status: 'request',
      href: '/features/invoices-claims',
      color: 'orange',
      category: 'paid'
    },
    {
      icon: CreditCard,
      title: 'Payment Follow-Up Assistant',
      description: 'Track outstanding payments and automate follow-up reminders.',
      status: 'request',
      href: '/features/payment-followup',
      color: 'orange',
      category: 'paid'
    },
    {
      icon: Package,
      title: 'Job Completion Pack Assistant',
      description: 'Compile all job documentation into a professional completion pack.',
      status: 'request',
      href: '/features/completion-pack',
      color: 'orange',
      category: 'paid'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation - Consistent with Landing Page */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-sm font-medium tracking-wide"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5"
            >
              <Share2 className="w-4 h-4" />
              SHARE
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]"
            >
              LOG IN
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-xs font-medium px-2"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="p-1.5 text-slate-600 hover:text-slate-900"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3"
            >
              LOG IN
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider">
              <Shield className="w-4 h-4" />
              YOUR ON-CALL CONTRACTS ADMINISTRATOR
            </div>
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              FEATURES BUILT FOR<br />
              <span className="text-orange-500">HOW TRADIES WORK</span>
            </h1>
            <p className="text-slate-300 text-sm md:text-base max-w-2xl mx-auto">
              Voice-first capture, AI-powered organization, and outputs that help you win work, 
              deliver jobs, and get paid faster.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          {/* Win Work */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                WIN THE WORK
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {features.filter(f => f.category === 'win').map((feature, i) => (
                <FeatureCard key={i} {...feature} />
              ))}
            </div>
          </div>

          {/* Deliver Work */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                DELIVER THE WORK
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.filter(f => f.category === 'deliver').map((feature, i) => (
                <FeatureCard key={i} {...feature} />
              ))}
            </div>
          </div>

          {/* Get Paid */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <Banknote className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                GET PAID
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.filter(f => f.category === 'paid').map((feature, i) => (
                <FeatureCard key={i} {...feature} onRequestClick={() => setShowSetupModal(true)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              READY TO SIMPLIFY<br />
              <span className="text-orange-500">YOUR ADMIN?</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Join tradies who are spending less time on paperwork and more time on the tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => setShowSetupModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-8 py-4 font-bold text-lg"
              >
                REGISTER INTEREST
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800 rounded-sm px-8 py-4 font-semibold"
              >
                LOG IN
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 2Tap. Powered for Smart Business.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Setup Modal */}
      <RequestSetupModal 
        isOpen={showSetupModal} 
        onClose={() => setShowSetupModal(false)} 
      />

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
      />
    </div>
  );
};

export default Features;
